import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { message, Spin, Tooltip } from 'antd';
import moment from 'moment';
import { CaretLeft, CaretRight, Info } from '@phosphor-icons/react';

import ModalWrapper from './ModalWrapper';
import { faxLocalDigits } from '../../utils/identifierTokens';
import { captureAnalytics } from '../../actions/preload';
import COLORS from '../../util/constants/colors';
import './MergeRecordsConfirm.scss';

// Display a stored fax value as xxx-xxx-xxxx (e.g. 929-508-0263) for
// readability, dropping any leading +1/1 US country code. Falls back to the
// raw value when it isn't a clean 10-digit number. Display-only — the value
// sourced/sent to the backend is never mutated.
const formatFaxDashed = (value) => {
  if (!value) return value;
  const local = faxLocalDigits(value);
  if (local.length !== 10) return value;
  return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
};

// Documents shown per matrix page. Pagination (with a prev/next pager) kicks in
// only when there are more documents than this, so the modal width stays bounded
// regardless of how many docs are merged.
const DOCS_PER_PAGE = 4;

// The retained fields the user can re-source. `key` matches the backend
// `field_sources` shape. `label` is the on-screen row label. Array order is the
// on-screen row order (Category is rendered first, separately).
const RETAINED_FIELDS = [
  { key: 'patient', label: 'Patient' },
  { key: 'dob', label: 'Date of birth' },
  { key: 'document_title', label: 'Document title' },
  { key: 'document_date', label: 'Document date' },
  { key: 'fax_number', label: 'Fax number' },
];

// The Category row is special: it has no `field_sources` entry (its value lives
// in selectedCategoryId / document_category_id).
const CATEGORY_ROW = { key: 'category', label: 'Category' };

// Plain-language explanation of the priority order the backend uses to pick the
// document every field is pre-selected from. Mirrors
// Receivers::MergeTargets.default_source_record — keep the two in sync.
const DEFAULT_SOURCE_TOOLTIP = (
  <div className="merge-records-confirm__tip">
    <div className="merge-records-confirm__tip-title">How defaults are picked</div>
    <div>
      Values are pre-selected from the first document that matches, in order:
    </div>
    <ol className="merge-records-confirm__tip-list">
      <li>A referral</li>
      <li>A document already filed in the EMR chart</li>
      <li>A document with both a patient name and date of birth</li>
      <li>The oldest document</li>
    </ol>
    <div className="merge-records-confirm__tip-note">
      When more than one document qualifies, the oldest of them is used.
    </div>
  </div>
);

class MergeRecordsConfirm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loadingPreview: true,
      previewError: false,
      objectClass: null,
      documents: [],
      selectedCategoryId: null,
      // token per retained field; defaults to the backend's default source
      // record (default_source_token).
      fieldSources: {},
      // Backend-chosen defaults, retained for confirm-time analytics only.
      defaultSourceToken: null,
      defaultCategoryId: null,
      merging: false,
      // 0-indexed page of the matrix's document columns. Clamped on render so it
      // can never point past the last page.
      summaryPage: 0,
    };
  }

  async componentDidMount() {
    const { getMergePreview, modalType } = this.props;
    const tokens = modalType?.data?.tokens || [];
    const response = await getMergePreview(tokens);

    if (!response || response.error || !response.data) {
      this.setState({ loadingPreview: false, previewError: true });
      return;
    }

    const {
      documents = [],
      default_document_category_id: defaultCategoryId = null,
      default_source_token: defaultSourceToken = null,
      object_class: objectClass = null,
    } = response.data;

    // Every field defaults to the backend's chosen default source record
    // (default_source_token, picked by a priority hierarchy). Fall back to the
    // first (oldest) document's token if the backend didn't supply one.
    const defaultToken = defaultSourceToken || documents[0]?.token;
    const fieldSources = {};
    RETAINED_FIELDS.forEach(({ key }) => {
      fieldSources[key] = defaultToken;
    });

    this.setState({
      loadingPreview: false,
      previewError: false,
      objectClass,
      documents,
      selectedCategoryId: defaultCategoryId,
      fieldSources,
      // Retain the backend's defaults so confirm-time analytics can report
      // whether the user re-sourced any field / changed the category.
      defaultSourceToken: defaultToken,
      defaultCategoryId,
    });
  }

  // Records the user opening the default-source explanation. onVisibleChange
  // fires on both open and close, so close is ignored to keep one event per
  // view. No properties: the copy is static, so there is nothing
  // document-specific (and certainly no patient data) to report.
  handleHierarchyVisibleChange = (visible) => {
    if (!visible) return;
    captureAnalytics('merge_confirm_hierarchy_viewed');
  };

  handleFieldSourceChange = (fieldKey, token) => {
    this.setState((prevState) => ({
      fieldSources: { ...prevState.fieldSources, [fieldKey]: token },
    }));
  };

  buildOverrides = () => {
    const { selectedCategoryId, fieldSources, objectClass } = this.state;
    const fieldSourcePayload = {};

    RETAINED_FIELDS.forEach(({ key }) => {
      // EmailAttachment has no fax number to source or set.
      if (key === 'fax_number' && objectClass === 'EmailAttachment') return;
      const selection = fieldSources[key];
      if (selection) fieldSourcePayload[key] = selection;
    });

    return {
      document_category_id: selectedCategoryId,
      field_sources: fieldSourcePayload,
    };
  };

  handleConfirm = async () => {
    const {
      mergeReferrals, hideModal, modalType,
    } = this.props;
    const tokens = modalType?.data?.tokens || [];
    const onMerged = modalType?.data?.onMerged;
    const { loadingPreview, previewError } = this.state;

    // Guard against confirming before the preview resolves. The Merge button is
    // also disabled while loading, but this keeps the handler robust regardless
    // of how it's invoked.
    if (loadingPreview) return undefined;

    // Fire before the network call so the interaction is recorded even if the
    // merge later fails. document_count is the number of docs being merged;
    // used_non_default_source flags whether the user re-sourced any retained
    // field or changed the category away from the backend defaults. Properties
    // are counts/booleans only — no patient data, tokens, or category names.
    const {
      fieldSources, selectedCategoryId, defaultSourceToken, defaultCategoryId,
    } = this.state;
    const usedNonDefaultSource = (
      (selectedCategoryId != null && selectedCategoryId !== defaultCategoryId)
      || Object.values(fieldSources).some((token) => token && token !== defaultSourceToken)
    );
    captureAnalytics('merge_confirm_submitted', {
      document_count: tokens.length,
      used_non_default_source: usedNonDefaultSource,
    });

    this.setState({ merging: true });

    // When the preview failed to load we can't collect overrides, so merge with
    // none — the backend still applies the referral-wins default category.
    const overrides = previewError ? null : this.buildOverrides();
    const response = await mergeReferrals(tokens, overrides);

    if (!response || response.error) {
      message.error({ content: 'We were unable to merge the selected records. Please try again.' });
      this.setState({ merging: false });
      return response;
    }

    hideModal();
    if (typeof onMerged === 'function') await onMerged(response);
    return response;
  };

  renderCategoryLabel = (category) => {
    if (!category) return '';
    return category.referral ? `${category.label} (Referral)` : category.label;
  };

  // The visible field rows, top-to-bottom: Category first, then RETAINED_FIELDS.
  // Fax is dropped entirely for EmailAttachment (no fax concept there).
  getFieldRows = () => {
    const { objectClass } = this.state;
    const rows = [CATEGORY_ROW, ...RETAINED_FIELDS];
    if (objectClass === 'EmailAttachment') {
      return rows.filter((row) => row.key !== 'fax_number');
    }
    return rows;
  };

  // The formatted display value a given document contributes for a field, or
  // null when the document has no value for it.
  cellValueFor = (fieldKey, doc) => {
    if (!doc) return null;
    switch (fieldKey) {
      case 'category':
        return doc.document_category ? this.renderCategoryLabel(doc.document_category) : null;
      case 'patient':
        return doc.patient_name;
      case 'dob':
        return doc.patient_dob ? moment(doc.patient_dob, 'YYYY-MM-DD').format('MMM D, YYYY') : null;
      case 'document_title':
        return doc.document_title;
      case 'document_date':
        return doc.document_date;
      case 'fax_number':
        return formatFaxDashed(doc.fax_number);
      default:
        return null;
    }
  };

  // Whether a given cell (row × document) is the currently-selected source.
  // Category: the doc carries the chosen category id (several docs can share it).
  // Others: the field's source token equals this doc's token.
  isCellSelected = (fieldKey, doc) => {
    const { fieldSources, selectedCategoryId } = this.state;
    if (fieldKey === 'category') {
      return Boolean(doc.document_category && doc.document_category.id === selectedCategoryId);
    }
    return fieldSources[fieldKey] === doc.token;
  };

  // Clicking a body cell re-sources that one row to the clicked document.
  // Category cells without a category are inert (rendered disabled).
  handleCellSelect = (fieldKey, doc, index) => {
    // Category cells without a category are inert — no selection, no event.
    if (fieldKey === 'category' && !doc.document_category) return;

    // Record the re-source. Only the field row key and the 0-based document
    // position travel with the event — never patient values, tokens, or names.
    captureAnalytics('merge_confirm_source_selected', {
      field: fieldKey,
      document_index: index,
    });

    if (fieldKey === 'category') {
      this.setState({ selectedCategoryId: doc.document_category.id });
      return;
    }
    this.handleFieldSourceChange(fieldKey, doc.token);
  };

  // Move the matrix's document columns one page in either direction, clamped.
  goToSummaryPage = (delta) => {
    this.setState((prevState) => {
      const pageCount = Math.max(1, Math.ceil(prevState.documents.length / DOCS_PER_PAGE));
      const next = Math.min(Math.max(prevState.summaryPage + delta, 0), pageCount - 1);
      return { summaryPage: next };
    });
  };

  // One matrix body cell: a borderless, full-width button. Clicking anywhere in
  // it re-sources the row to this document. Native <button> gives keyboard
  // (Enter/Space) activation + a focus ring for free.
  renderCell = (row, doc, index) => {
    const raw = this.cellValueFor(row.key, doc);
    const isEmpty = raw === null || raw === undefined || raw === '';
    const selected = this.isCellSelected(row.key, doc);
    const disabled = row.key === 'category' && !doc.document_category;
    const shown = isEmpty ? 'Not provided' : raw;

    const classes = ['merge-records-confirm__cell'];
    if (selected) classes.push('merge-records-confirm__cell--selected');
    if (isEmpty) classes.push('merge-records-confirm__cell--empty');

    return (
      <td className="merge-records-confirm__cell-wrap" key={doc.token}>
        <button
          type="button"
          className={classes.join(' ')}
          data-testid={`merge-cell-${row.key}-${index}`}
          aria-pressed={selected}
          aria-label={`${row.label}, Doc ${index + 1}: ${shown}`}
          disabled={disabled}
          onClick={() => this.handleCellSelect(row.key, doc, index)}
          title={shown}
        >
          {shown}
        </button>
      </td>
    );
  };

  // The document × value matrix: rows are the retained values, columns are the
  // documents (paginated at DOCS_PER_PAGE). The row-label column is fixed; only
  // the document columns paginate.
  renderMatrix = () => {
    const { documents, summaryPage } = this.state;
    const rows = this.getFieldRows();

    const paginate = documents.length > DOCS_PER_PAGE;
    const pageCount = Math.max(1, Math.ceil(documents.length / DOCS_PER_PAGE));
    const page = Math.min(Math.max(summaryPage, 0), pageCount - 1);
    const start = page * DOCS_PER_PAGE;
    const visibleDocs = paginate
      ? documents.slice(start, start + DOCS_PER_PAGE)
      : documents;
    const rangeStart = start + 1;
    const rangeEnd = start + visibleDocs.length;

    return (
      <div>
        <div className="merge-records-confirm__eyebrow">
          <span>{`Merging ${documents.length} documents`}</span>
          <Tooltip
            title={DEFAULT_SOURCE_TOOLTIP}
            overlayStyle={{ maxWidth: 320 }}
            // rc-tooltip defaults to ['hover'], which would leave the
            // explanation mouse-only; 'focus' opens it on Tab as well.
            trigger={['hover', 'focus']}
            onVisibleChange={this.handleHierarchyVisibleChange}
          >
            <button
              type="button"
              className="merge-records-confirm__eyebrow-info"
              data-testid="merge-default-source-info"
              aria-label="How default values are picked"
            >
              <Info size={14} weight="bold" color={COLORS.TEXT_TERTIARY_LITE} />
            </button>
          </Tooltip>
        </div>
        <div className="merge-records-confirm__hint">
          Select a value in any row to choose which document the merged record
          inherits it from.
        </div>
        <div className="merge-records-confirm__matrix-wrap">
          <table className="merge-records-confirm__matrix">
            <colgroup>
              <col className="merge-records-confirm__matrix-rowlabel-col" />
              {visibleDocs.map((doc) => <col key={doc.token} />)}
            </colgroup>
            <thead>
              <tr>
                <th className="merge-records-confirm__matrix-corner">
                  <span className="merge-records-confirm__sr-only">Field</span>
                </th>
                {visibleDocs.map((doc, i) => {
                  const index = start + i;
                  return (
                    <th key={doc.token}>
                      <span className="merge-records-confirm__dochead">
                        <span className="merge-records-confirm__dochead-badge">{index + 1}</span>
                        <span className="merge-records-confirm__dochead-label">{`Doc ${index + 1}`}</span>
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td className="merge-records-confirm__rowlabel">{row.label}</td>
                  {visibleDocs.map((doc, i) => this.renderCell(row, doc, start + i))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {paginate && (
          <div className="merge-records-confirm__pager">
            <button
              type="button"
              className="merge-records-confirm__pager-btn"
              onClick={() => this.goToSummaryPage(-1)}
              disabled={page === 0}
              aria-label="Previous documents"
            >
              <CaretLeft size={14} weight="bold" />
            </button>
            <span className="merge-records-confirm__pager-count">
              {`Docs ${rangeStart}–${rangeEnd} of ${documents.length}`}
            </span>
            <button
              type="button"
              className="merge-records-confirm__pager-btn"
              onClick={() => this.goToSummaryPage(1)}
              disabled={page >= pageCount - 1}
              aria-label="Next documents"
            >
              <CaretRight size={14} weight="bold" />
            </button>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { hideModal, modalType } = this.props;
    const { loadingPreview, previewError, merging } = this.state;

    const customContentStyle = { maxWidth: '92vw' };

    // "Merge & Archive" flow (flag set by the toolbar handler) relabels the
    // modal title and primary button; the archive step itself is handled by the
    // onMerged continuation, so no behavior changes here.
    const archiveAfter = modalType?.data?.archiveAfter === true;
    const modalTitle = archiveAfter ? 'Merge & Archive' : 'Merge Records';
    const actionName = archiveAfter ? 'Merge & Archive' : 'Merge';

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        width={600}
        wrapClassName="merge-records-confirm-modal"
        action={this.handleConfirm}
        actionName={actionName}
        dismiss="Cancel"
        modalTitle={modalTitle}
        isLoading={merging}
        disabled={loadingPreview}
        isDraggable
      >
        {loadingPreview && (
          <div style={{ textAlign: 'center', padding: '24px' }}>
            <Spin />
          </div>
        )}

        {!loadingPreview && previewError && (
          <p>
            We could not load the document details. You can still merge the
            records; the oldest document supplies the retained values and a
            referral category is kept when any selected document is a referral.
          </p>
        )}

        {!loadingPreview && !previewError && (
          <div>
            {this.renderMatrix()}
          </div>
        )}
      </ModalWrapper>
    );
  }
}

MergeRecordsConfirm.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  getMergePreview: PropTypes.func.isRequired,
  mergeReferrals: PropTypes.func.isRequired,
};

export default MergeRecordsConfirm;
